import { UseDataQueryOptions } from '../../../hooks/useDataQuery/dataQuery.types';
import useDataQuery from '../../../hooks/useDataQuery/useDataQuery';
import { useAuthContext } from '../../../modules/login/AuthContext';
import { GetOrderConditionDTO } from '../../../modules/orders/conditions/dtos/orderCondition.dto';
import OrderConditionCustomFieldConfig from '../../../modules/orders/conditions/interfaces/orderConditionCustomFieldConfig.interface';
import {
  getFindOrderConditionFreightQuotesKey,
  getFindOrderConditionsCustomFieldsKey,
  getFindOrderConditionsKey,
} from './orderCondition.keys';
import {
  findOrderConditionFreightQuotes,
  findOrderConditions,
  findOrderConditionsCustomFields,
} from './orderCondition.repository';
import {
  FindOrderConditionFreightQuotesParams,
  FindOrderConditionParams,
  OrderConditionFreightQuote,
} from './orderCondition.types';

export const useFindOrderConditions = (
  { isOfflineMode, ...params }: FindOrderConditionParams,
  options?: UseDataQueryOptions,
) => {
  const { loggedin } = useAuthContext();

  return useDataQuery<GetOrderConditionDTO[] | undefined>(
    {
      gcTime: 0,
      queryFn: async () => {
        const res = await findOrderConditions({ isOfflineMode, ...params });

        if (!res.ok) {
          throw res.originalError;
        }

        return res.data;
      },
      queryKey: getFindOrderConditionsKey({ orderId: params.orderId }),
    },
    { ...options, enabled: loggedin },
  );
};

export const useFindOrderConditionFreightQuotes = (
  params: FindOrderConditionFreightQuotesParams,
  options?: UseDataQueryOptions,
) => {
  const { loggedin } = useAuthContext();

  return useDataQuery<OrderConditionFreightQuote[]>(
    {
      queryFn: async () => {
        const response = await findOrderConditionFreightQuotes(params);

        if (!response.ok || !response.data) {
          throw response.originalError;
        }

        return response.data;
      },
      queryKey: getFindOrderConditionFreightQuotesKey(params),
      refetchOnMount: false,
      gcTime: 0,
    },
    { retry: false, ...options, enabled: Boolean(options?.enabled) && loggedin },
  );
};

export const useFindOrderConditionsCustomFields = () => {
  const { loggedin } = useAuthContext();

  return useDataQuery<OrderConditionCustomFieldConfig[]>(
    {
      queryFn: async () => {
        const response = await findOrderConditionsCustomFields();
        if (!response.ok) {
          throw response.originalError;
        }
        return response.data ?? [];
      },
      queryKey: getFindOrderConditionsCustomFieldsKey(),
    },
    { enabled: loggedin },
  );
};
